import React from 'react';

// Helper functions
const formatCurrency = (value) => {
  if (!value) return '0';
  return `${parseFloat(value).toFixed(2)}`;
};

const formatQuantity = (value) => {
  if (!value) return '0';
  return parseFloat(value).toFixed(2);
};

const calculatePieceCount = (totalQuantity, weightPerPiece) => {
  if (!totalQuantity || !weightPerPiece || weightPerPiece === 0) return 0;
  const pieces = parseFloat(totalQuantity) / parseFloat(weightPerPiece);
  if (Math.abs(pieces - Math.round(pieces)) < 0.000001) {
    return Math.round(pieces);
  }
  return pieces.toFixed(2);
};

const formatPieceCount = (value) => {
  if (!value) return '0';
  const num = parseFloat(value);
  if (Math.abs(num - Math.round(num)) < 0.000001) {
    return Math.round(num).toString();
  }
  return num.toFixed(2);
};

const isReelOrTota = (categoryKey) => {
  const lower = categoryKey.toLowerCase();
  return lower === 'reel' || lower === 'tota';
};

const isRoll = (categoryKey) => {
  return categoryKey.toLowerCase() === 'roll';
};

const isPacket = (categoryKey) => {
  return categoryKey.toLowerCase() === 'packet';
};

// Calculate category totals
const calculateCategoryTotals = (categoryKey, categoryData) => {
  let totalQuantity = 0;
  let totalValue = 0;
  const lower = categoryKey.toLowerCase();
  
  if (isReelOrTota(lower)) {
  Object.values(categoryData).forEach(productData => {
    Object.values(productData).forEach(weightData => {
      Object.values(weightData).forEach(lengthData => {
        Object.values(lengthData).forEach(vendorData => {

          const qty = parseFloat(vendorData.quantity) || 0;
          totalQuantity += qty; // ✅ FIXED

          totalValue += parseFloat(vendorData.totalPrice) || 0;
        });
      });
    });
  });
} else if (isRoll(lower)) {
    Object.values(categoryData).forEach(productData => {
      Object.values(productData).forEach(sizeData => {
        if (sizeData.quantity !== undefined) {
          totalQuantity += parseFloat(sizeData.quantity) || 0;
          totalValue += parseFloat(sizeData.totalPrice) || 0;
        } else {
          Object.values(sizeData).forEach(vendorData => {
            totalQuantity += parseFloat(vendorData.quantity) || 0;
            totalValue += parseFloat(vendorData.totalPrice) || 0;
          });
        }
      });
    });
  } else if (isPacket(lower)) {
    Object.values(categoryData).forEach(productData => {
      Object.values(productData).forEach(variantData => {
        totalQuantity += parseFloat(variantData.quantity) || 0;
        totalValue += parseFloat(variantData.totalPrice) || 0;
      });
    });
  } else {
    Object.values(categoryData).forEach(productData => {
      totalQuantity += parseFloat(productData.quantity) || 0;
      totalValue += parseFloat(productData.totalPrice) || 0;
    });
  }
  return { totalQuantity, totalValue };
};

// Reel/Tota Table Component
const ReelTotaTable = ({ productName, productData }) => {
  // Sort numerically
  const sortNumerically = (arr) => {
    return [...arr].sort((a, b) => parseFloat(a) - parseFloat(b));
  };

  // Pehle saare sizes collect karo
  const allSizes = new Set();
  const allWeights = new Set();
  
  Object.keys(productData).forEach(weight => {
    allWeights.add(weight);
    Object.keys(productData[weight]).forEach(size => {
      allSizes.add(size);
    });
  });

  // Sizes ko sort karo (18, 19, 20, 21...)
  const sortedSizes = sortNumerically(Array.from(allSizes));
  const sortedWeights = sortNumerically(Array.from(allWeights));

  if (sortedSizes.length === 0) {
    return React.createElement('div', { style: { padding: '20px', textAlign: 'center', color: '#999' } }, 'No stock available');
  }

  let productTotalPieces = 0;
  let productTotalValue = 0;
  const tableRows = [];

  // Pehle size ke hisaab se loop, phir weight ke hisaab se
  sortedSizes.forEach((sizeKey) => {
    sortedWeights.forEach((weightKey) => {
      const weightData = productData[weightKey];
      if (!weightData || !weightData[sizeKey]) return;
      
      const sizeData = weightData[sizeKey];
      const weightPerPiece = parseFloat(weightKey);
      
      // Get vendor keys
      const vendorKeys = Object.keys(sizeData).sort((a, b) => a.localeCompare(b));
      
      // For each vendor
      vendorKeys.forEach((vendorName, vendorIdx) => {
        const vendorData = sizeData[vendorName];
        const totalQuantity = parseFloat(vendorData.quantity) || 0;
        const pieceCount = calculatePieceCount(totalQuantity, weightPerPiece);
        const rate = parseFloat(vendorData.rate) || 0;
        const totalValue = parseFloat(vendorData.totalPrice) || 0;
        
        productTotalPieces += pieceCount;
        productTotalValue += totalValue;
        
        const cells = [];
        
        // Add size cell (only for first vendor of this size)
        if (vendorIdx === 0) {
          cells.push(
            React.createElement('td', { 
              key: 'size', 
              rowSpan: vendorKeys.length, 
              style: { 
                border: '1px solid #ddd', 
                padding: '10px', 
                verticalAlign: 'middle', 
                fontWeight: '500', 
                backgroundColor: '#f9f9f9' 
              } 
            }, sizeKey)
          );
        }
        
        // Add weight cell (only for first vendor of this weight)
        if (vendorIdx === 0) {
          cells.push(
            React.createElement('td', { 
              key: 'weight', 
              rowSpan: vendorKeys.length, 
              style: { 
                border: '1px solid #ddd', 
                padding: '10px', 
                verticalAlign: 'middle', 
                fontWeight: '500', 
                backgroundColor: '#f9f9f9' 
              } 
            }, weightKey)
          );
        }
        
        cells.push(
          React.createElement('td', { 
            key: 'vendor', 
            style: { border: '1px solid #ddd', padding: '10px' } 
          }, vendorName),
          React.createElement('td', { 
            key: 'quantity', 
            style: { border: '1px solid #ddd', padding: '10px', textAlign: 'right' } 
          }, formatPieceCount(pieceCount)),
          React.createElement('td', { 
            key: 'rate', 
            style: { border: '1px solid #ddd', padding: '10px', textAlign: 'right' } 
          }, formatCurrency(rate)),
          React.createElement('td', { 
            key: 'total', 
            style: { border: '1px solid #ddd', padding: '10px', textAlign: 'right', fontWeight: '500', color: '#0a8938' } 
          }, formatCurrency(totalValue))
        );
        
        tableRows.push(
          React.createElement('tr', { 
            key: `${sizeKey}-${weightKey}-${vendorName}`, 
            style: { backgroundColor: vendorIdx % 2 === 0 ? '#fff' : '#fafafa' } 
          }, cells)
        );
      });
    });
  });
  
  // Footer
  const footer = [];
  if (productTotalPieces > 0) {
    footer.push(
      React.createElement('tfoot', { key: 'tfoot' },
        React.createElement('tr', { 
          style: { 
            backgroundColor: '#e8f5e9', 
            fontWeight: 'bold', 
            borderTop: '2px solid #0a8938' 
          } 
        },
          React.createElement('td', { 
            colSpan: 3, 
            style: { border: '1px solid #ddd', padding: '10px', textAlign: 'right' } 
          }, `Total for ${productName}:`),
          React.createElement('td', { 
            style: { border: '1px solid #ddd', padding: '10px', textAlign: 'right' } 
          }, `${formatPieceCount(productTotalPieces)}`),
          React.createElement('td', { 
            style: { border: '1px solid #ddd', padding: '10px', textAlign: 'right' } 
          }, ''),
          React.createElement('td', { 
            style: { border: '1px solid #ddd', padding: '10px', textAlign: 'right', color: '#0a8938' } 
          }, formatCurrency(productTotalValue))
        )
      )
    );
  }

  return React.createElement('div', { style: { overflowX: 'auto' } },
    React.createElement('table', { style: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' } },
      React.createElement('thead', {},
        React.createElement('tr', { style: { backgroundColor: '#f5f5f5' } },
          React.createElement('th', { style: { border: '1px solid #ddd', padding: '10px', textAlign: 'left' } }, 'Size'),
          React.createElement('th', { style: { border: '1px solid #ddd', padding: '10px', textAlign: 'left' } }, 'Weight (kg)'),
          React.createElement('th', { style: { border: '1px solid #ddd', padding: '10px', textAlign: 'left' } }, 'Vendor'),
          React.createElement('th', { style: { border: '1px solid #ddd', padding: '10px', textAlign: 'right' } }, 'Quantity'),
          React.createElement('th', { style: { border: '1px solid #ddd', padding: '10px', textAlign: 'right' } }, 'Rate'),
          React.createElement('th', { style: { border: '1px solid #ddd', padding: '10px', textAlign: 'right' } }, 'Total Value')
        )
      ),
      React.createElement('tbody', {}, tableRows),
      footer
    )
  );
};

// Roll Table Component
const RollTable = ({ productName, productData }) => {
  const sizes = Object.entries(productData);
  
  if (sizes.length === 0) {
    return React.createElement('div', { style: { padding: '20px', textAlign: 'center', color: '#999' } }, 'No stock available');
  }

  let productTotalQty = 0;
  let productTotalVal = 0;
  const tableRows = [];

  sizes.forEach(([sizeKey, sizeData]) => {
    let quantity = 0;
    let rate = 0;
    let totalValue = 0;
    
    if (sizeData.quantity !== undefined) {
      quantity = parseFloat(sizeData.quantity) || 0;
      rate = parseFloat(sizeData.rate) || 0;
      totalValue = parseFloat(sizeData.totalPrice) || 0;
    } else {
      const values = Object.values(sizeData);
      for (const vendorData of values) {
        quantity += parseFloat(vendorData.quantity) || 0;
        totalValue += parseFloat(vendorData.totalPrice) || 0;
        rate = parseFloat(vendorData.rate) || 0;
      }
    }
    
    productTotalQty += quantity;
    productTotalVal += totalValue;
    
    tableRows.push(React.createElement('tr', { key: sizeKey, style: { backgroundColor: '#fff' } },
      React.createElement('td', { style: { border: '1px solid #ddd', padding: '10px', fontWeight: '500' } }, sizeKey),
      React.createElement('td', { style: { border: '1px solid #ddd', padding: '10px', textAlign: 'right' } }, formatQuantity(quantity)),
      React.createElement('td', { style: { border: '1px solid #ddd', padding: '10px', textAlign: 'right' } }, formatCurrency(rate)),
      React.createElement('td', { style: { border: '1px solid #ddd', padding: '10px', textAlign: 'right', fontWeight: '500', color: '#0a8938' } }, formatCurrency(totalValue))
    ));
  });

  const footer = [];
  if (productTotalQty > 0) {
    footer.push(React.createElement('tfoot', { key: 'tfoot' },
      React.createElement('tr', { style: { backgroundColor: '#e8f5e9', fontWeight: 'bold', borderTop: '2px solid #0a8938' } },
        React.createElement('td', { colSpan: 1, style: { border: '1px solid #ddd', padding: '10px', textAlign: 'right' } }, `Total for ${productName}:`),
        React.createElement('td', { style: { border: '1px solid #ddd', padding: '10px', textAlign: 'right' } }, formatQuantity(productTotalQty)),
        React.createElement('td', { style: { border: '1px solid #ddd', padding: '10px', textAlign: 'right' } }, ''),
        React.createElement('td', { style: { border: '1px solid #ddd', padding: '10px', textAlign: 'right', color: '#0a8938' } }, formatCurrency(productTotalVal))
      )
    ));
  }

  return React.createElement('div', { style: { overflowX: 'auto' } },
    React.createElement('table', { style: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' } },
      React.createElement('thead', {},
        React.createElement('tr', { style: { backgroundColor: '#f5f5f5' } },
          React.createElement('th', { style: { border: '1px solid #ddd', padding: '10px', textAlign: 'left' } }, 'Size'),
          React.createElement('th', { style: { border: '1px solid #ddd', padding: '10px', textAlign: 'right' } }, 'Quantity'),
          React.createElement('th', { style: { border: '1px solid #ddd', padding: '10px', textAlign: 'right' } }, 'Rate'),
          React.createElement('th', { style: { border: '1px solid #ddd', padding: '10px', textAlign: 'right' } }, 'Total Value')
        )
      ),
      React.createElement('tbody', {}, tableRows),
      footer
    )
  );
};

// Packet Table Component
const PacketTable = ({ productName, variants }) => {
  if (Object.keys(variants).length === 0) {
    return React.createElement('div', { style: { padding: '20px', textAlign: 'center', color: '#999' } }, 'No stock available');
  }

  let productTotalQty = 0;
  let productTotalVal = 0;
  const tableRows = [];

  Object.entries(variants).forEach(([variantKey, variantData], idx) => {
    const [size, ktae, gram] = variantKey.split('|');
    const qty = parseFloat(variantData.quantity) || 0;
    const val = parseFloat(variantData.totalPrice) || 0;
    productTotalQty += qty;
    productTotalVal += val;
    
    tableRows.push(React.createElement('tr', { key: variantKey, style: { backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa' } },
      React.createElement('td', { style: { border: '1px solid #ddd', padding: '10px' } }, size),
      React.createElement('td', { style: { border: '1px solid #ddd', padding: '10px' } }, ktae),
      React.createElement('td', { style: { border: '1px solid #ddd', padding: '10px' } }, gram),
      React.createElement('td', { style: { border: '1px solid #ddd', padding: '10px', textAlign: 'right' } }, formatQuantity(variantData.quantity)),
      React.createElement('td', { style: { border: '1px solid #ddd', padding: '10px', textAlign: 'right' } }, formatCurrency(variantData.rate)),
      React.createElement('td', { style: { border: '1px solid #ddd', padding: '10px', textAlign: 'right', fontWeight: '500', color: '#0a8938' } }, formatCurrency(variantData.totalPrice))
    ));
  });

  const footer = [];
  if (productTotalQty > 0) {
    footer.push(React.createElement('tfoot', { key: 'tfoot' },
      React.createElement('tr', { style: { backgroundColor: '#e8f5e9', fontWeight: 'bold', borderTop: '2px solid #0a8938' } },
        React.createElement('td', { colSpan: 3, style: { border: '1px solid #ddd', padding: '10px', textAlign: 'right' } }, `Total for ${productName}:`),
        React.createElement('td', { style: { border: '1px solid #ddd', padding: '10px', textAlign: 'right' } }, formatQuantity(productTotalQty)),
        React.createElement('td', { style: { border: '1px solid #ddd', padding: '10px', textAlign: 'right' } }, ''),
        React.createElement('td', { style: { border: '1px solid #ddd', padding: '10px', textAlign: 'right', color: '#0a8938' } }, formatCurrency(productTotalVal))
      )
    ));
  }

  return React.createElement('div', { style: { overflowX: 'auto' } },
    React.createElement('table', { style: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' } },
      React.createElement('thead', {},
        React.createElement('tr', { style: { backgroundColor: '#f5f5f5' } },
          React.createElement('th', { style: { border: '1px solid #ddd', padding: '10px', textAlign: 'left' } }, 'Size'),
          React.createElement('th', { style: { border: '1px solid #ddd', padding: '10px', textAlign: 'left' } }, 'Ktae'),
          React.createElement('th', { style: { border: '1px solid #ddd', padding: '10px', textAlign: 'left' } }, 'Gram'),
          React.createElement('th', { style: { border: '1px solid #ddd', padding: '10px', textAlign: 'right' } }, 'Quantity'),
          React.createElement('th', { style: { border: '1px solid #ddd', padding: '10px', textAlign: 'right' } }, 'Rate'),
          React.createElement('th', { style: { border: '1px solid #ddd', padding: '10px', textAlign: 'right' } }, 'Total Value')
        )
      ),
      React.createElement('tbody', {}, tableRows),
      footer
    )
  );
};

// Simple Category Table Component
const SimpleTable = ({ categoryData }) => {
  let totalQty = 0;
  let totalVal = 0;
  const tableRows = [];
  const entries = Object.entries(categoryData);

  entries.forEach(([productName, productData], idx) => {
    const qty = parseFloat(productData.quantity) || 0;
    const val = parseFloat(productData.totalPrice) || 0;
    totalQty += qty;
    totalVal += val;
    
    tableRows.push(React.createElement('tr', { key: productName, style: { backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa' } },
      React.createElement('td', { style: { border: '1px solid #ddd', padding: '10px', fontWeight: '500' } }, productName),
      React.createElement('td', { style: { border: '1px solid #ddd', padding: '10px', textAlign: 'right' } }, formatQuantity(productData.quantity)),
      React.createElement('td', { style: { border: '1px solid #ddd', padding: '10px', textAlign: 'right' } }, formatCurrency(productData.rate)),
      React.createElement('td', { style: { border: '1px solid #ddd', padding: '10px', textAlign: 'right', fontWeight: '500', color: '#0a8938' } }, formatCurrency(productData.totalPrice))
    ));
  });

  const footer = [];
  if (totalQty > 0) {
    footer.push(React.createElement('tfoot', { key: 'tfoot' },
      React.createElement('tr', { style: { backgroundColor: '#e8f5e9', fontWeight: 'bold' } },
        React.createElement('td', { colSpan: 1, style: { border: '1px solid #ddd', padding: '10px', textAlign: 'right' } }, 'Total for Category:'),
        React.createElement('td', { style: { border: '1px solid #ddd', padding: '10px', textAlign: 'right' } }, formatQuantity(totalQty)),
        React.createElement('td', { style: { border: '1px solid #ddd', padding: '10px', textAlign: 'right' } }, ''),
        React.createElement('td', { style: { border: '1px solid #ddd', padding: '10px', textAlign: 'right', color: '#0a8938' } }, formatCurrency(totalVal))
      )
    ));
  }

  return React.createElement('div', { style: { overflowX: 'auto' } },
    React.createElement('table', { style: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' } },
      React.createElement('thead', {},
        React.createElement('tr', { style: { backgroundColor: '#f5f5f5' } },
          React.createElement('th', { style: { border: '1px solid #ddd', padding: '10px', textAlign: 'left' } }, 'Product Name'),
          React.createElement('th', { style: { border: '1px solid #ddd', padding: '10px', textAlign: 'right' } }, 'Quantity'),
          React.createElement('th', { style: { border: '1px solid #ddd', padding: '10px', textAlign: 'right' } }, 'Rate'),
          React.createElement('th', { style: { border: '1px solid #ddd', padding: '10px', textAlign: 'right' } }, 'Total Value')
        )
      ),
      React.createElement('tbody', {}, tableRows),
      footer
    )
  );
};

// Main Component
const InventoryTables = ({ data, selectedFilter, filterType = 'length' }) => {
  if (!data || Object.keys(data).length === 0) {
    return React.createElement('div', { style: { textAlign: 'center', padding: '40px', backgroundColor: '#f8f9fa', borderRadius: '8px' } },
      React.createElement('p', { style: { color: '#666' } }, 'No inventory data found for the selected filters.'),
      React.createElement('p', { style: { fontSize: '12px', color: '#999' } }, 'Try selecting a different category, product, or month.')
    );
  }

  const sections = [];
  
  Object.entries(data).forEach(([categoryKey, categoryData]) => {
    const lowerKey = categoryKey.toLowerCase();
    const totals = calculateCategoryTotals(categoryKey, categoryData);
    
    const productDivs = [];
    
    Object.entries(categoryData).forEach(([productName, productData]) => {
      let tableComponent = null;
      
      // Check if productData has actual data (not empty)
      const hasData = Object.keys(productData).length > 0;
      
      if (!hasData) {
        return;
      }
      
      if (isReelOrTota(lowerKey)) {
        tableComponent = React.createElement(ReelTotaTable, {
          key: productName,
          productName: productName,
          productData: productData
        });
      } else if (isRoll(lowerKey)) {
        tableComponent = React.createElement(RollTable, {
          key: productName,
          productName: productName,
          productData: productData
        });
      } else if (isPacket(lowerKey)) {
        tableComponent = React.createElement(PacketTable, {
          key: productName,
          productName: productName,
          variants: productData
        });
      } else {
        tableComponent = React.createElement(SimpleTable, {
          key: productName,
          categoryData: { [productName]: productData }
        });
      }
      
      productDivs.push(
        React.createElement('div', { key: productName, style: { marginBottom: '25px', border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden' } },
          React.createElement('div', { style: { backgroundColor: '#e8f5e9', padding: '8px 15px', fontWeight: 'bold', fontSize: '16px', borderBottom: '2px solid #0a8938' } },
            `📦 ${productName}`
          ),
          tableComponent
        )
      );
    });
    
    // Only add section if there are products
    if (productDivs.length > 0) {
      sections.push(
        React.createElement('div', { key: categoryKey, style: { marginBottom: '30px' } },
          React.createElement('h3', { style: { 
            backgroundColor: '#0a8938', 
            color: 'white', 
            padding: '10px 15px', 
            borderRadius: '6px',
            marginBottom: '15px',
            fontSize: '18px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          } },
            React.createElement('span', {}, `${categoryKey.toUpperCase()}${isReelOrTota(lowerKey) ? ' (Weight → Size → Vendors)' : ''}`),
            React.createElement('span', { style: { fontSize: '14px', fontWeight: 'normal' } },
              `Total: ${isReelOrTota(lowerKey) ? `${formatPieceCount(totals.totalQuantity)}` : `${formatQuantity(totals.totalQuantity)} units`} | ${formatCurrency(totals.totalValue)}`
            )
          ),
          productDivs
        )
      );
    }
  });

  return React.createElement('div', {}, sections);
};

export default InventoryTables;